// src/pages/VerifyEmail.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import logoImg from '../assets/images/Logo-Font-Color-NB.png';

const VerifyEmail: React.FC = () => {
  const { currentUser, logoutUser } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleResendVerification = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      
      const auth = getAuth();
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setMessage('Verification email has been resent. Please check your inbox.');
      } else {
        throw new Error('User not logged in');
      }
    } catch (err: unknown) {
      setError('Failed to send verification email. Please try again later.');
      console.error('Email verification error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    // Refresh user data
    getAuth().currentUser?.reload().then(() => {
      if (getAuth().currentUser?.emailVerified) {
        navigate('/today');
      } else {
        setError('Your email is not verified yet. Please complete verification before continuing.');
      }
    }).catch(err => {
      console.error('Error refreshing user data:', err);
      setError('Unable to verify your email status. Please try again later.');
    });
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 content-normal">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src={logoImg}
            alt="Orlo Logo"
            className="mx-auto h-16 w-auto"
          />
          <h2 className="mt-4 text-2xl font-semibold text-neutral-800">
            Verify your email
          </h2>
          <p className="mt-2 text-base text-neutral-600">
            We've sent a verification email to {currentUser?.email}
          </p>
        </div>
        
        {error && (
          <div className="bg-error-100 border border-error-200 text-error-800 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {message && (
          <div className="bg-success-100 border border-success-200 text-success-800 px-4 py-3 rounded-md">
            {message}
          </div>
        )}
        
        <div className="bg-sky-50 border-l-4 border-sky-500 p-4 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-sky-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-sky-700">
                Please follow the instructions in the email to complete verification. If you can't find the email, check your spam folder.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-8 space-y-4">
          <button
            onClick={handleResendVerification}
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-sky-500 hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-70"
          >
            {isSubmitting ? 'Sending...' : 'Resend verification email'}
          </button>
          
          <button
            onClick={handleContinue}
            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-brand-blue bg-white border-brand-blue hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue"
          >
            I've verified my email, continue
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full flex justify-center py-2 px-4 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue"
          >
            Sign out
          </button>
        </div>
        
        <div className="mt-6 text-center text-sm text-neutral-600">
          <p>Need help? Contact <a href="mailto:support@orlo.app" className="font-medium text-brand-blue hover:text-brand-purple">support@orlo.app</a></p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;